import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs';

import {
  LiveScoreRow,
  LiveScorecardsPageResponse,
  LiveScoreMatchPayload,
  LiveScoreSocketEnvelope,
} from '../../../models/common/live-score-match.model';
import { CommonServiceService } from '../../../utilities/services/common-service/common-service.service';
import { WebsocketService } from '../../../utilities/services/websocket-service/websocket.service';
import { SkeletonLoaderComponent } from '../../shared/skeleton-loader/skeleton-loader.component';
import { CommonScoreCardsComponent } from '../../shared/common-score-cards/common-score-cards.component';
import { CommonSearchBarComponent } from '../../shared/common-search-bar/common-search-bar.component';
import { MobileSearchComponent } from '../../shared/mobile-search/mobile-search.component';
import { LiveScorecardsListService } from './live-scorecards-list.service';
import { TemplateService } from '../../../utilities/services/template/template.service';

/**
 * Renders the tenant live scorecards grid: paginated REST load, websocket
 * reconciliation, search, and infinite scroll.
 */
@Component({
  selector: 'stupa-scorecard-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CommonSearchBarComponent,
    CommonScoreCardsComponent,
    SkeletonLoaderComponent,
    TranslateModule,
    MobileSearchComponent,
  ],
  templateUrl: './scorecards-list.component.html',
  styleUrl: './scorecards-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LiveScorecardsListService],
})
export class ScorecardListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly commonservice = inject(CommonServiceService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly websocketService = inject(WebsocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly listService = inject(LiveScorecardsListService);
  private readonly templateService = inject(TemplateService);

  /** True when the OTT layout (template 1) is active — see {@link TemplateService}. */
  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }

  @Input() user_role_id = 0;
  @Input() is_sub_license = false;

  searchQuery = '';
  rows = 40;
  pageNumber = 1;
  screenWidth = 0;
  hasMoreData = true;
  isLoadingMore = false;
  mobile_search = false;
  loading = false;
  searchBarExpanded = false;

  /** Placeholder count for the template-1 loading skeleton grid (two rows at the desktop 4-column width). */
  readonly t1ScorecardSkeletonCards: ReadonlyArray<number> = [0, 1, 2, 3, 4, 5, 6, 7];

  private scrollTicking = false;
  /** Debounced search stream — avoids firing REST on every keystroke. */
  private readonly searchInput$ = new Subject<string>();
  private readonly SEARCH_DEBOUNCE_MS = 300;

  /** Rows bound by the template — delegated to the list service. */
  get live_scored_matches_list(): LiveScoreRow[] {
    return this.listService.liveScoredMatchesList;
  }

  /** Row ids currently showing the completed removal overlay. */
  get removingRowIds(): Set<string> {
    return this.listService.removingRowIds;
  }

  constructor() {
    this.screenWidth = document.documentElement.clientWidth;
  }

  ngOnInit(): void {
    this.listService.configure({
      onStateChange: () => this.cdr.markForCheck(),
      isSubLicense: () => this.is_sub_license,
      userRoleId: () => this.user_role_id,
    });
    this.subscribeToSocket();
    this.setupDebouncedSearch();
    this.getLiveScorecards();
  }

  /**
   * Debounces search input, cancels stale requests, then reloads page 1.
   */
  private setupDebouncedSearch(): void {
    this.searchInput$
      .pipe(
        debounceTime(this.SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap((query) => {
          this.searchQuery = query;
          this.mobile_search = false;
          this.pageNumber = 1;
          this.hasMoreData = true;
          this.listService.resetList();
          this.loading = true;
          this.cdr.markForCheck();
        }),
        // Cancel the previous in-flight search when the user keeps typing.
        switchMap((query) =>
          this.commonservice.get_live_scored_mtches(
            null,
            '',
            query,
            this.pageNumber,
            this.rows,
            null,
            this.user_role_id,
            true,
            true,
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: any) => {
          const page = result as LiveScorecardsPageResponse;
          const rawData = page.data?.data ?? [];
          this.listService.mergeRestPage(rawData, false);
          this.hasMoreData = rawData.length === this.rows;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  /** Subscribes to live score websocket updates for this list instance. */
  private subscribeToSocket(): void {
    this.websocketService.messages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: LiveScoreSocketEnvelope | null) => {
          if (result == null) {
            this.getLiveScorecards();
          } else {
            this.updateMatchData(result.data);
          }
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => undefined,
        complete: () => undefined,
      });
  }

  /**
   * Loads live scorecards from REST — initial page or infinite-scroll page.
   *
   * @param isLoadMore - When true, appends the next page instead of replacing.
   */
  getLiveScorecards(isLoadMore = false): void {
    if (isLoadMore) {
      this.isLoadingMore = true;
    } else {
      this.loading = true;
    }

    this.commonservice
      .get_live_scored_mtches(
        null,
        '',
        this.searchQuery,
        this.pageNumber,
        this.rows,
        null,
        this.user_role_id,
        true,
        true,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          const page = result as LiveScorecardsPageResponse;
          const rawData = page.data?.data ?? [];
          this.listService.mergeRestPage(rawData, isLoadMore);
          this.hasMoreData = rawData.length === this.rows;
          this.loading = false;
          this.isLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (isLoadMore) {
            this.pageNumber--;
          }
          this.loading = false;
          this.isLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.screenWidth = document.documentElement.clientWidth;
    this.cdr.markForCheck();
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.scrollTicking) {
      return;
    }
    this.scrollTicking = true;

    requestAnimationFrame(() => {
      this.scrollTicking = false;
      if (this.isLoadingMore || this.loading || !this.hasMoreData) {
        return;
      }

      const scrollPosition = window.innerHeight + window.scrollY;
      if (scrollPosition >= document.documentElement.scrollHeight * 0.8) {
        this.pageNumber++;
        this.getLiveScorecards(true);
      }
    });
  }

  /**
   * Reconciles websocket payload(s) against the current list.
   *
   * @param message - Single match update or batch from the socket envelope.
   */
  updateMatchData(
    message: LiveScoreMatchPayload | LiveScoreMatchPayload[],
  ): void {
    this.listService.updateMatchData(message);
  }

  /** Navigates to the event detail route for a scorecard row. */
  openEvent(id: number): void {
    this.router.navigate(['events', id, 0, 0, '5', '5']);
  }

  /** Queues a debounced search reload — API fires after typing pauses. */
  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.searchInput$.next(query);
  }

  /** Tracks mobile search bar expand/collapse for header layout. */
  onSearchBarInputExpanded(expanded: boolean): void {
    this.searchBarExpanded = expanded;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.listService.destroy();
  }

  /**
   * Stable trackBy for scorecard rows — prefers `__row_id`, then match `id`.
   *
   * @param index - Fallback index when no stable id is present.
   * @param item - Row or match object from the list.
   * @returns Value used as the ngFor track key.
   */
  trackById(index: number, item: LiveScoreRow | LiveScoreMatchPayload): string | number {
    if ('__row_id' in item && item.__row_id != null) {
      return item.__row_id;
    }
    if ('id' in item && item.id != null) {
      return item.id;
    }
    return index;
  }
}